/*
 *
 * BranchOfficeReport
 *
 */

import moment from 'moment';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import CopyIcon from '@mui/icons-material/FileCopy';

import { useTheme } from '@mui/material';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import { useBranchReport } from '~/hooks/api/useBranchReport';
import { useBranchReports } from '~/hooks/api/useBranchReports';
import Spacings from '~/modules/ui-kit/spacings';
import messages from './messages';

const route = getRouteApi('/reports/$reportType');

export const BranchOfficeReport = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const search = route.useSearch();
  const year = search.year;
  const month = search.month;
  const { data: branchReport, isFetching } = useBranchReport(
    year ?? 0,
    month ?? 0
  );
  const { data: availableReports, isFetching: isFetchingAvailableReports } =
    useBranchReports();
  const theme = useTheme();
  const intl = useIntl();
  if (!isFetchingAvailableReports && availableReports?.length === 0) {
    return (
      <Spacings.Inset>
        <Typography color="textSecondary">
          <FormattedMessage {...messages.noReportsAvailable} />
        </Typography>
      </Spacings.Inset>
    );
  }
  const prepareData = () =>
    branchReport
      ? [
          {
            label: <FormattedMessage {...messages.participatingPioneers} />,
            key: 'participatingPioneers',
            value: branchReport.participatingPioneers,
          },
          {
            label: <FormattedMessage {...messages.participatingPublishers} />,
            key: 'participatingPublishers',
            value: branchReport.participatingPublishers,
          },
          {
            label: <FormattedMessage {...messages.missingReports} />,
            key: 'missingReports',
            value: branchReport.missingReports,
          },
          {
            label: <FormattedMessage {...messages.reportsFromPreviousMonths} />,
            key: 'reportsFromPreviousMonths',
            value: branchReport.reportsFromPreviousMonths,
          },
          {
            label: <FormattedMessage {...messages.publisherHours} />,
            key: 'publisherHours',
            value: branchReport.publisherHours,
          },
        ]
      : [];

  const handleCopyReportData = () => {
    navigator.clipboard
      .writeText(
        prepareData()
          .map(({ value }) => intl.formatNumber(value ?? 0))
          .join('\n')
      )
      .then(() => {
        showSnackbar({
          message: intl.formatMessage(messages.copySuccessful),
        });
      });
  };

  const handleSelectMonth = (event: SelectChangeEvent<number | undefined>) => {
    navigate({
      to: '/reports/$reportType',
      params: { reportType: 'branch' },
      search: { year, month: Number(event.target.value), quarter: undefined },
    });
  };

  const handleSelectYear = (event: SelectChangeEvent<number | undefined>) => {
    navigate({
      to: '/reports/$reportType',
      params: { reportType: 'branch' },
      search: { year: Number(event.target.value), month, quarter: undefined },
    });
  };

  const selectedMonthLabel = month ? moment.months()[month - 1] : '';
  return (
    <Spacings.Inset>
      <Spacings.Stack>
        <Spacings.Inline>
          {!isFetchingAvailableReports && (
            <>
              <FormControl variant="standard" style={{ minWidth: 120 }}>
                <InputLabel id="year-select-label">
                  <FormattedMessage {...messages.year} />
                </InputLabel>
                <Select
                  variant="standard"
                  labelId="year-select-label"
                  id="year-select"
                  value={year}
                  onChange={handleSelectYear}
                >
                  {availableReports
                    ?.reduce<number[]>(
                      (allYears, report) =>
                        report.year && allYears.includes(report.year)
                          ? allYears
                          : [...allYears, report.year!],
                      []
                    )
                    .map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl
                variant="standard"
                style={{
                  minWidth: 120,
                  marginLeft: theme.spacing(2),
                }}
              >
                <InputLabel id="month-select-label">
                  <FormattedMessage {...messages.month} />
                </InputLabel>
                <Select
                  variant="standard"
                  labelId="month-select-label"
                  id="month-select"
                  value={month}
                  onChange={handleSelectMonth}
                >
                  {availableReports
                    ?.filter((report) => report.year === year)
                    .map((report) => (
                      <MenuItem
                        key={report.monthInYear}
                        value={report.monthInYear}
                      >
                        {report.monthInYear}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </>
          )}
        </Spacings.Inline>

        {!isFetching && branchReport && (
          <TableContainer style={{ marginTop: theme.spacing(2) }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ paddingLeft: 0 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<CopyIcon />}
                      onClick={handleCopyReportData}
                    >
                      <FormattedMessage {...messages.copyReport} />
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" color="textSecondary">
                      {selectedMonthLabel}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prepareData().map(({ label, value, key }) => (
                  <TableRow key={key}>
                    <TableCell>
                      <Typography color="textSecondary">{label}</Typography>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      <Typography>
                        <FormattedNumber value={value ?? 0} />
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Spacings.Stack>
    </Spacings.Inset>
  );
};

BranchOfficeReport.displayName = 'BranchOfficeReport';

export default BranchOfficeReport;
